import { AxiosResponse } from "axios";
import { call, put, select, takeLatest } from "redux-saga/effects";

import {
	ChannelsComplexFilter,
	ComplexFilterItem,
	ComplexFilterType,
	DateComplexFilter,
	PropertyIdsComplexFilter,
} from "interfaces/filters";

import { Property } from "models/properties";
import { PropertyKPIData, GroupedPropertyReport } from "interfaces/reports";

import * as dashboardActions from "state/modules/dashboard/actions";

import ReportsClient from "services/api/reports";
import { loadProperties } from "state/modules/properties";

import {
	getDashboardFilter,
	getDashboardSelectedYear,
	DashboardActionTypes,
	getKPISortBy,
} from "state/modules/dashboard";
import { defaultReportDateFilter } from "utils/report";
import { expandChannelsForBackend } from "utils/channels";

function compileProperties(propertyFilter: PropertyIdsComplexFilter): string {
	if (
		propertyFilter?.value &&
		propertyFilter.value.filter(Boolean).length > 0
	) {
		const nonEmptyValues = propertyFilter.value.filter(Boolean);
		return nonEmptyValues.join(",");
	}
	return "";
}

const extractKPIDataFromGroupedReport = (
	groupedReport: GroupedPropertyReport,
	propertyMap: Map<string, string>
): PropertyKPIData[] =>
	Object.entries(groupedReport).map(([propertyId, report]) => {
		const { calculations } = report;
		const totalInCurrency = calculations?.totalInCurrency || {};
		const totalIncome = (totalInCurrency as any)?.totalIncomeInCurrency || 0;
		const totalExpenses = (totalInCurrency as any)?.totalExpensesInCurrency || 0;

		return {
			propertyId,
			propertyName: propertyMap.get(propertyId) || propertyId,
			income: totalIncome,
			payouts: (totalInCurrency as any)?.netOwnerIncomeInCurrency || 0,
			commissions: (totalInCurrency as any)?.totalHoteliseFeeInCurrency || 0,
			expenses: totalExpenses,
			cleaning: (totalInCurrency as any)?.totalCleaningInCurrency || 0,
			adr: (totalInCurrency as any)?.adr || 0,
			revPar: (totalInCurrency as any)?.revPar || 0,
			occupancy: calculations?.occupancyRate || 0,
			alos: calculations?.alos || 0,
			ownerBlockedDays: calculations?.reservationPeriod?.ownerBlockedDays || 0,
		};
	});

export function* handleGetDashboardReport(): Generator {
	yield put(dashboardActions.getDashboardReportStart());
	const filter = (yield select(getDashboardFilter)) as ComplexFilterItem[];

	try {
		const properties = (yield call(loadProperties)) as Property[];
		const propertyIds = properties.map((item) => item.id);
		const propertyIdsString = propertyIds.join(",");

		const dateFilter = filter.find(
			(item) => item.type === ComplexFilterType.DATE
		) as DateComplexFilter;

		const propertyFilter = filter.find(
			(item) => item.type === ComplexFilterType.PROPERTY_ID
		) as PropertyIdsComplexFilter;

		const channelsFilter = filter.find(
			(item) => item.type === ComplexFilterType.CHANNEL
		) as ChannelsComplexFilter;

		const fieldsToGetBack = [
			"calculations.totalExpensesPerCurrency",
			"calculations.totalCleaningPerCurrency",
			"calculations.totalHoteliseFeePerCurrency",
			"calculations.totalInCurrency",
			"calculations.occupancyRate",
			"calculations.alos",
			"calculations.reservationPeriod",
			"calculations.totalIncomePerCurrency",
			"calculations.channelDistribution",
		];
		const res = (yield call(ReportsClient.generateReport, {
			propertyIds: compileProperties(propertyFilter) || propertyIdsString,
			dateFrom: dateFilter?.value?.dateFrom || defaultReportDateFilter.dateFrom,
			dateTo: dateFilter?.value?.dateTo || defaultReportDateFilter.dateTo,
			dateFromType:
				dateFilter?.value?.dateFromType || defaultReportDateFilter.dateFromType,
			dateToType:
				dateFilter?.value?.dateToType || defaultReportDateFilter.dateToType,
			fields: fieldsToGetBack.join(","),
			channels: channelsFilter?.value?.length
				? expandChannelsForBackend(channelsFilter.value).join(",")
				: "",
		})) as AxiosResponse;

		yield put(dashboardActions.setDashboardReport(res.data.content));
	} catch (error) {
		console.log({ error });
		yield put(dashboardActions.getDashboardReportFail());
	}
}

export function* handleGetDashboardYearReport(): Generator {
	yield put(dashboardActions.getDashboardYearReportStart());

	const year = (yield select(getDashboardSelectedYear)) as number;
	const filter = (yield select(getDashboardFilter)) as ComplexFilterItem[];

	try {
		const properties = (yield call(loadProperties)) as Property[];
		const propertyIds = properties.map((item) => item.id);
		const propertyIdsString = propertyIds.join(",");

		const propertyFilter = filter.find(
			(item) => item.type === ComplexFilterType.PROPERTY_ID
		) as PropertyIdsComplexFilter;

		const channelsFilter = filter.find(
			(item) => item.type === ComplexFilterType.CHANNEL
		) as ChannelsComplexFilter;

		const fieldsToGetBack = [
			"calculations.totalExpensesPerCurrency",
			"calculations.totalCleaningPerCurrency",
			"calculations.totalHoteliseFeePerCurrency",
			"calculations.totalInCurrency",
			"calculations.occupancyRate",
			"calculations.alos",
			"calculations.reservationPeriod",
		];
		const res = (yield call(ReportsClient.getFullYearReport, {
			year,
			propertyIds: compileProperties(propertyFilter) || propertyIdsString,
			fields: fieldsToGetBack.join(","),
			channels: channelsFilter?.value?.length
				? expandChannelsForBackend(channelsFilter.value).join(",")
				: undefined,
		})) as AxiosResponse;

		yield put(dashboardActions.setDashboardYearReport(res.data.content));
	} catch (error) {
		console.log({ error });
		yield put(dashboardActions.getDashboardYearReportFail());
	}
}

export function* handleGetDashboardKPIReport(): Generator {
	yield put(dashboardActions.getDashboardKPIReportStart());
	const filter = (yield select(getDashboardFilter)) as ComplexFilterItem[];
	const sortBy = (yield select(getKPISortBy)) as 'highest' | 'lowest';

	try {
		const dateFilter = filter.find(
			(item) => item.type === ComplexFilterType.DATE
		) as DateComplexFilter;

		const propertyFilter = filter.find(
			(item) => item.type === ComplexFilterType.PROPERTY_ID
		) as PropertyIdsComplexFilter;

		const properties = (yield call(loadProperties)) as Property[];
		const propertyIds = properties.map((item) => item.id);
		const propertyIdsString = propertyIds.join(",");
		const propertyMap = new Map<string, string>(
			properties.map((p) => [p.id, p.name])
		);

		const fieldsToGetBack = [
			"calculations.totalExpensesPerCurrency",
			"calculations.totalCleaningPerCurrency",
			"calculations.totalHoteliseFeePerCurrency",
			"calculations.totalInCurrency",
			"calculations.occupancyRate",
			"calculations.alos",
			"calculations.totalIncomePerCurrency",
			"calculations.reservationPeriod",
		];

		const res = (yield call(ReportsClient.generateGroupedReport, {
			propertyIds: compileProperties(propertyFilter) || propertyIdsString,
			dateFrom: dateFilter?.value?.dateFrom || defaultReportDateFilter.dateFrom,
			dateTo: dateFilter?.value?.dateTo || defaultReportDateFilter.dateTo,
			dateFromType:
				dateFilter?.value?.dateFromType || defaultReportDateFilter.dateFromType,
			dateToType:
				dateFilter?.value?.dateToType || defaultReportDateFilter.dateToType,
			fields: fieldsToGetBack.join(","),
		})) as AxiosResponse;

		const groupedReport = res.data.content as GroupedPropertyReport;
		yield put(dashboardActions.setDashboardKPIReport(groupedReport));

		const kpiData = extractKPIDataFromGroupedReport(groupedReport, propertyMap);
		kpiData.sort((a, b) =>
			sortBy === 'highest' ? b.income - a.income : a.income - b.income
		);

		yield put(dashboardActions.setDashboardKPIData(kpiData));
	} catch (error) {
		console.log({ error });
		yield put(dashboardActions.getDashboardKPIReportFail());
	}
}

export function* dashboardSaga(): Generator {
	yield takeLatest(
		DashboardActionTypes.GET_DASHBOARD_REPORT,
		handleGetDashboardReport
	);
	yield takeLatest(
		DashboardActionTypes.GET_DASHBOARD_YEAR_REPORT,
		handleGetDashboardYearReport
	);
	yield takeLatest(
		DashboardActionTypes.GET_DASHBOARD_KPI_REPORT,
		handleGetDashboardKPIReport
	);

	// Re-fetch KPI data when filters change
	yield takeLatest(DashboardActionTypes.SET_DASHBOARD_FILTER, function* () {
		yield put(dashboardActions.getDashboardKPIReport());
	});
}
